import { useEffect, useRef } from "react";
import { DEEP_LINK_FOCUS_FLASH_MS, flashElement } from "./flashElement";

/**
 * Shared post-navigation "hint" hook (generalised from `useTicketFocus`).
 *
 * Pass the deep-link `focus` key (`<subject_type>:<id>[:version]`). The hook
 * finds the element rendering `data-focus-ref="<focusKey>"`, scrolls it into
 * view, and flashes it once via `data-focus-flash="true"` (keyframes live in
 * `app/styles/animations.css`). The lookup retries briefly so it still lands
 * when the target list is still loading on cold deep links.
 *
 * It is a no-op when `focusKey` is empty. Pass ``focusSeq`` (bumped on repeat
 * navigation to the same row) to re-run the flash on an already-focused target.
 */
const FOCUS_ATTR = "data-focus-ref";
export { DEEP_LINK_FOCUS_FLASH_MS };
const DEFAULT_DURATION_MS = DEEP_LINK_FOCUS_FLASH_MS;
const RETRY_WINDOW_MS = 6000;
const RETRY_INTERVAL_MS = 90;

export function useDeepLinkFocus(
  focusKey: string | null | undefined,
  options: {
    durationMs?: number;
    focusSeq?: string | null;
    enabled?: boolean;
    /** Search within this scrollport (avoids flashing the wrong duplicate row). */
    root?: HTMLElement | null;
    /** When false, only flash — virtual list already scrolled the row into view. */
    scrollIntoView?: boolean;
  } = {},
): void {
  const handled = useRef<string | null>(null);
  const {
    durationMs = DEFAULT_DURATION_MS,
    focusSeq = null,
    enabled = true,
    root = null,
    scrollIntoView = true,
  } = options;

  useEffect(() => {
    if (!enabled || !focusKey) return;
    const token = `${focusKey}\0${focusSeq ?? ""}`;
    if (handled.current === token) return;
    handled.current = token;

    const start = Date.now();
    let retryTimer = 0;
    let cancelFlash: (() => void) | undefined;
    let rafId = 0;

    const findElement = (): HTMLElement | null => {
      const selector = `[${FOCUS_ATTR}="${CSS.escape(focusKey)}"]`;
      const scope = root ?? document;
      const matches = scope.querySelectorAll<HTMLElement>(selector);
      for (const element of matches) {
        if (element.closest('[aria-hidden="true"]')) {
          continue;
        }
        return element;
      }
      return null;
    };

    const attempt = () => {
      const element = findElement();
      if (!element) {
        if (Date.now() - start < RETRY_WINDOW_MS) {
          retryTimer = window.setTimeout(attempt, RETRY_INTERVAL_MS);
        }
        return;
      }
      cancelFlash = flashElement(element, { durationMs, scrollIntoView });
    };

    // Wait for virtual list scroll-to-index + row mount.
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(attempt, 80);
      });
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (retryTimer) window.clearTimeout(retryTimer);
      cancelFlash?.();
    };
  }, [focusKey, focusSeq, enabled, durationMs, root, scrollIntoView]);
}
